/**
 * "How conventional is the harmony I want?" — asked before the first chord.
 *
 * This is the drawer's adventurousness slider, met earlier. Same range, same step, same
 * live word for where it sits (`adventurousnessLabel`), and the caller dispatches the same
 * action, so there is one setting with two doors rather than two settings that disagree.
 * The heading says "Harmony" rather than "Adventurousness" because on this screen the
 * question is what kind of session to have, and the two ends say it in the player's terms:
 * familiar at one, adventurous at the other — the latter being the drawer's own word.
 */

import { useId } from 'react';
import { adventurousnessLabel } from '../../commands/localCommands.js';
import { ADVENTUROUSNESS_STEP } from '../sheets/SettingsDrawer.js';

export interface HarmonySliderProps {
  /** 0..1, `state.adventurousness`. */
  value: number;
  onChange: (value: number) => void;
}

export function HarmonySlider({ value, onChange }: HarmonySliderProps): React.JSX.Element {
  const id = useId();
  return (
    <div className="hc-harmony" data-testid="hc-home-harmony">
      {/* The value sits beside the label, not inside it: inside, it became part of the
          input's accessible name, which then changed as the thumb moved and read the value
          twice alongside `aria-valuetext`. The name stays "Harmony", like the drawer's. */}
      <div className="hc-harmony__label">
        <label htmlFor={id}>Harmony</label>
        <span
          className="hc-harmony__value"
          data-testid="hc-home-adventurousness-label"
          aria-hidden="true"
        >
          {adventurousnessLabel(value)}
        </span>
      </div>
      <input
        id={id}
        className="hc-harmony__slider"
        data-testid="hc-home-adventurousness"
        type="range"
        min={0}
        max={1}
        step={ADVENTUROUSNESS_STEP}
        value={value}
        aria-valuetext={adventurousnessLabel(value)}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      {/* Decoration for the eye; the input's own label and value text carry it for a reader. */}
      <div className="hc-harmony__ends" aria-hidden="true">
        <span>familiar</span>
        <span>adventurous</span>
      </div>
    </div>
  );
}
